import Phaser from 'phaser';
import { Dir } from '../../sim/direction';
import { saves } from '../records';
import { ownMowerTexture } from '../render/cosmeticArt';

/** Height of one mowed band, in game pixels. */
const BAND = 24;
/** How long one band takes to cross the screen, and the delay between bands. */
const TRAVEL_MS = 230;
const STAGGER_MS = 14;
/** The reveal: the stripes lift off in the same direction, faster. */
const REVEAL_MS = 170;
const FADE_MS = 140;
const STRIPES = [0x2f7d34, 0x266a2b] as const;
const stripe = (i: number) => (i % 2 === 0 ? STRIPES[0] : STRIPES[1]);

type Phase = 'idle' | 'cover' | 'reveal';

/**
 * Scene transitions as a mowing wipe (docs/ART_DIRECTION.md, motion): a row of mowers sweeps the
 * screen in alternating stripes, the next screen starts underneath, then the stripes lift away.
 * With reduced motion it is a quick fade. Always running, drawn above every other scene while a
 * wipe is in progress. Use {@link wipeTo}.
 */
export class WipeScene extends Phaser.Scene {
  private g!: Phaser.GameObjects.Graphics;
  private mowers: Phaser.GameObjects.Image[] = [];
  private phase: Phase = 'idle';
  private t0 = 0;
  private fade = false;
  private action: (() => void) | null = null;

  constructor() {
    super('Wipe');
  }

  create(): void {
    this.g = this.add.graphics();
  }

  /** True while the screen is being covered (the next screen has not started yet). */
  get covering(): boolean {
    return this.phase === 'cover';
  }

  /**
   * Covers the screen, runs `action` (which starts the next scene) underneath, then reveals. A
   * new wipe may start during a reveal (a quick tap on the new screen); one already covering wins.
   */
  run(action: () => void): void {
    if (this.covering) return;
    this.action = action;
    this.fade = saves().save.settings.reducedShake;
    this.phase = 'cover';
    this.t0 = this.time.now;
    this.scene.bringToTop();
    const texture = ownMowerTexture(this, saves().save.selectedMower, saves().save);
    const bands = this.bandCount();
    for (const m of this.mowers) m.destroy();
    this.mowers = this.fade
      ? []
      : Array.from({ length: bands }, (_, i) =>
          this.add.image(0, i * BAND + BAND / 2, texture, i % 2 === 0 ? Dir.E : Dir.W),
        );
    this.draw();
  }

  override update(): void {
    if (this.phase === 'idle') return;
    // Scenes started by the action (the HUD, say) bring themselves to the top: stay above them.
    // Only reorder when something has moved above.
    if (this.scene.getIndex() !== this.game.scene.getScenes(false).length - 1) {
      this.scene.bringToTop();
    }
    this.draw();
  }

  private bandCount(): number {
    return Math.ceil(this.scale.gameSize.height / BAND);
  }

  private coverMs(): number {
    return this.fade ? FADE_MS : TRAVEL_MS + STAGGER_MS * (this.bandCount() - 1);
  }

  private revealMs(): number {
    return this.fade ? FADE_MS : REVEAL_MS + STAGGER_MS * (this.bandCount() - 1);
  }

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const elapsed = this.time.now - this.t0;
    const g = this.g.clear();
    if (this.phase === 'cover' && elapsed >= this.coverMs()) {
      const action = this.action;
      this.action = null;
      action?.();
      this.phase = 'reveal';
      this.t0 = this.time.now;
      for (const m of this.mowers) m.setVisible(false);
      this.drawFull(width, height);
      return;
    }
    if (this.phase === 'reveal' && elapsed >= this.revealMs()) {
      this.phase = 'idle';
      for (const m of this.mowers) m.destroy();
      this.mowers = [];
      return;
    }
    if (this.fade) {
      const k = Math.min(1, elapsed / FADE_MS);
      g.fillStyle(STRIPES[1], this.phase === 'cover' ? k : 1 - k).fillRect(0, 0, width, height);
      return;
    }
    const travel = this.phase === 'cover' ? TRAVEL_MS : REVEAL_MS;
    for (let i = 0; i < this.bandCount(); i++) {
      const local = Phaser.Math.Clamp((elapsed - i * STAGGER_MS) / travel, 0, 1);
      const reach = Math.round(local * (width + 16));
      const fromLeft = i % 2 === 0;
      const y = i * BAND;
      g.fillStyle(stripe(i));
      if (this.phase === 'cover') {
        // The stripe grows behind the mower.
        const w = Math.min(width, reach);
        g.fillRect(fromLeft ? 0 : width - w, y, w, BAND);
        const m = this.mowers[i];
        if (m) {
          m.setVisible(local > 0 && local < 1);
          m.setX(fromLeft ? reach - 4 : width - reach + 4);
        }
      } else {
        // The stripe lifts off from where it started.
        const gone = Math.min(width, reach);
        g.fillRect(fromLeft ? gone : 0, y, width - gone, BAND);
      }
    }
  }

  private drawFull(width: number, height: number): void {
    if (this.fade) {
      this.g.fillStyle(STRIPES[1]).fillRect(0, 0, width, height);
      return;
    }
    for (let i = 0; i < this.bandCount(); i++) {
      this.g.fillStyle(stripe(i)).fillRect(0, i * BAND, width, BAND);
    }
  }
}

/**
 * Leaves `from` with a mowing wipe: input to it stops at once, `action` runs when the screen is
 * covered (default: start scene `key` with `data`). Falls back to running it straight away if the
 * wipe scene is not available.
 */
export function wipeTo(from: Phaser.Scene, key: string, data?: object, action?: () => void): void {
  const wipe = from.scene.get('Wipe') as WipeScene | null;
  const go = () => {
    from.input.enabled = true; // a restarted scene keeps its input plugin
    if (action) action();
    else from.scene.start(key, data);
  };
  if (!wipe || !from.scene.isActive('Wipe')) {
    go();
    return;
  }
  if (wipe.covering) return;
  from.input.enabled = false;
  wipe.run(go);
}
